import clsx from 'clsx';
import { Formik, FormikProps } from 'formik';
import React, { useRef, useState } from 'react';
import { Form } from 'react-bootstrap';
import ReCAPTCHA from 'react-google-recaptcha';

import { SubscriptionFormProps } from '.';
import { useUserCreate } from '../../hooks';
import { RECAPTCHA_SITE_KEY } from '../../src/env';
import { extractQueryParams } from '../../utils/queryStringHelper';
import { Button, CustomSpinner } from '../BaseUI';
import { TextField } from '../FormFields';
import styles from './styles.module.scss';
import validationSchema from './validationSchema';

interface FormValues {
  email: string;
  name: string;
  prefilledUserEmail?: string;
}

interface SignupFormProps extends SubscriptionFormProps {
  setStripeCustomerId: (stripeCustomerId: string) => void;
}

const SignupForm = ({
  currentPlan: { plan },
  prefilledUserName,
  prefilledUserEmail,
  setStripeCustomerId,
}: SignupFormProps) => {
  const [disableInput, setDisableInput] = useState(false);
  const { mutateAsync: createUser, isLoading } = useUserCreate();
  const reCaptchaRef = useRef<ReCAPTCHA>();
  const formRef = useRef<FormikProps<FormValues>>();
  const { referring_article_id: referringArticleId } = extractQueryParams(['referring_article_id']);

  // Passed prefilledUserEmail to the form for email validation initial state
  const initialState = {
    email: prefilledUserEmail || '',
    name: prefilledUserName || '',
    prefilledUserEmail,
  };

  const handleCreateUser = async ({ email, name }: FormValues) => {
    try {
      const refToken = await reCaptchaRef.current.executeAsync();
      const createUserParams = {
        email,
        name,
        plan,
        recaptcha_token: refToken,
        referring_article_id: referringArticleId,
      };
      const { stripeCustomerId } = await createUser(createUserParams);
      setDisableInput(true);
      setStripeCustomerId(stripeCustomerId);
    } catch {
      setDisableInput(false);
      reCaptchaRef.current.reset();
    }
  };

  return (
    <div className={styles.subscriptionForm}>
      <ReCAPTCHA sitekey={RECAPTCHA_SITE_KEY} size="invisible" ref={reCaptchaRef}>
        <Formik
          enableReinitialize
          initialValues={initialState}
          onSubmit={handleCreateUser}
          validationSchema={validationSchema}
          innerRef={formRef}
        >
          {({ handleSubmit, isValid }) => (
            <Form className="account-info" noValidate onSubmit={handleSubmit}>
              <div className={styles.subscribePaymentHeading}>
                <div className={styles.subscribePaymentHeadingText}>Account Information</div>
                <div className={styles.subscribePaymentHeadingLine}></div>
              </div>
              <TextField
                name="name"
                placeholder="Full Name"
                disabled={prefilledUserName || disableInput}
              />
              <TextField
                name="email"
                placeholder="Email"
                disabled={prefilledUserEmail || disableInput}
              />
              <Button
                disabled={
                  isLoading ||
                  !isValid ||
                  disableInput ||
                  (!!prefilledUserName && !!prefilledUserEmail)
                }
                type="submit"
                className={clsx(styles.submitBtn, 'tw-mt-16')}
              >
                {isLoading ? (
                  <CustomSpinner
                    color="#fff"
                    size={10}
                    style={{ display: 'flex', justifyContent: 'center' }}
                  />
                ) : (
                  <>Continue</>
                )}
              </Button>
            </Form>
          )}
        </Formik>
      </ReCAPTCHA>
    </div>
  );
};

export default SignupForm;
